import cuid from "cuid";

import type { FileMetaType } from "@/hooks/UseTusHooks/Generics";
import { publishToWorkspaceFeed } from "@/models/actions/clientActions";

/** A file waiting in the composer. It uploads when Send is pressed, not before. */
export type ComposerAttachment = {
  file: File;
  /** The file's name, for the chip the composer shows while the file waits. */
  name: string;
  /**
   * The details `parseFile` read from the file, when the Attach popover already read them: an audio file's title and
   * description, an extension the user supplied. A pasted file carries none and is read at send.
   */
  meta?: FileMetaType;
};

/** What a composer holds for one channel: the typed text and, at most, one waiting file. */
export type ComposerDraft = {
  text: string;
  attachment: ComposerAttachment | null;
};

export const EMPTY_COMPOSER_DRAFT: ComposerDraft = Object.freeze({ text: "", attachment: null });

/** The longest text a composer sends; the driver list's footer holds the same line. */
export const COMPOSER_MAX_CHARS = 5000;

/** Whether the draft's text runs past the limit; the composer says so and holds the send. */
export function composerTextTooLong(draft: ComposerDraft): boolean {
  return draft.text.length > COMPOSER_MAX_CHARS;
}

/** Whether the draft has anything to send: text within the limit, or a file. */
export function composerDraftSendable(draft: ComposerDraft): boolean {
  if (composerTextTooLong(draft)) return false;
  return draft.text.trim().length > 0 || draft.attachment != null;
}

/** The publish a send goes through; the real one by default, a recorder in tests. */
export type ComposerPublish = (params: {
  workspaceId: string;
  feedId: string;
  itemId?: string;
  contentId: string;
  text?: string;
  isSilent?: boolean;
}) => Promise<unknown>;

/**
 * The upload a send puts its file through, resolving with the ids the upload service holds it under; the tus upload
 * from `useComposerUpload` in the app, a recorder in tests.
 */
export type ComposerUpload = (params: {
  attachment: ComposerAttachment;
  feedId: string;
}) => Promise<{ contentId: string; itemId: string }>;

/**
 * Sends a composer's draft to a channel: the text as one message, then the file as another, the way the classic
 * composer sends them. The text goes first so the driver hears the words before the file lands. The file uploads only
 * now, tagged with the channel, so the upload pipeline posts it with its artifact the moment it lands; the publish that
 * follows is the same upsert, made so the sender sees the item without waiting on the pipeline. The file item is
 * silent, as uploads are everywhere in the app (there is nothing to speak); the text takes the channel's own
 * `isSilent`. A draft with neither text nor file sends nothing.
 *
 * @param onTextPublished - Runs once the text is out, before the upload starts, so the caller can drop the text from
 * the draft and a retry after a failed upload does not send it twice.
 */
export async function sendComposerDraft({
  workspaceId,
  feedId,
  draft,
  isSilent,
  upload,
  publish = publishToWorkspaceFeed,
  onTextPublished,
}: {
  workspaceId: string;
  feedId: string;
  draft: ComposerDraft;
  /** The channel's silent setting, applied to the text. */
  isSilent: boolean | undefined;
  upload: ComposerUpload;
  publish?: ComposerPublish;
  onTextPublished?: () => void;
}): Promise<void> {
  const text = draft.text.trim();
  if (text) {
    await publish({ workspaceId, feedId, contentId: cuid(), text, isSilent });
    onTextPublished?.();
  }
  if (draft.attachment) {
    const { contentId, itemId } = await upload({ attachment: draft.attachment, feedId });
    await publish({ workspaceId, feedId, contentId, itemId, isSilent: true });
  }
}
